import { useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import { isEmpty, map, orderBy, upperCase, isEqual } from "lodash";
import {
  searchAppliedTags,
  getEntityTagCountsByType,
} from "src/queries/tags.graphql";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import {
  setTagsData,
  setError,
  setPagination,
  setSelectedTabType,
  setAvailableTabTypes,
} from "../actions/tagsActions";

/**
 * Custom hook to manage tags data fetching and state
 * @param {Object} params - Hook parameters
 * @param {string} params.entityId - Entity ID to fetch tags for
 * @param {Array} params.tagTypes - Array of tag types to fetch
 * @param {string} params.entityType - Type of entity (default: GROUP)
 * @param {Object} params.pagination - Current pagination state
 * @param {string} params.selectedTabType - Currently selected tab type
 * @param {Function} params.dispatch - Redux dispatch function
 * @returns {Object} Hook return values
 */
export const useTagsData = ({
  entityId,
  tagTypes,
  entityType = ENTITY_TYPES.GROUP,
  pagination,
  selectedTabType,
  dispatch,
}) => {
  const lastFetchedDataRef = useRef(null);

  // Query to get tag counts by type
  const { loading: countsLoading } = useQuery(getEntityTagCountsByType, {
    variables: {
      entityIds: [entityId],
      tagTypes: map(tagTypes, "id"),
      includeRemovedTags: false,
    },
    skip: !entityId || isEmpty(tagTypes),
    onCompleted: (response) => {
      const counts = response?.result?.counts || [];

      const availableTabs = tagTypes
        .filter((tagType) => {
          const count =
            counts.find((c) => c.tagType === tagType.id)?.count || 0;
          return count > 0;
        })
        .map((tagType) => ({
          id: tagType.id,
          name: tagType.name,
          count: counts.find((c) => c.tagType === tagType.id)?.count || 0,
        }));

      dispatch(setAvailableTabTypes(availableTabs));

      if (availableTabs.length > 0 && !selectedTabType) {
        dispatch(setSelectedTabType(availableTabs[0].id));
      }
    },
    onError: (queryError) => {
      dispatch(setError(queryError));
    },
  });

  // Memoize variables to prevent unnecessary refetches
  const searchAppliedTagsVariables = useMemo(
    () => ({
      entityIds: [entityId],
      tagTypes: selectedTabType ? [selectedTabType] : [],
      appliedToTypes: [upperCase(entityType)],
      size: pagination.limit,
      offset: pagination.offset,
      showAppliedCount: true,
      includeIndirectRelationships: true,
      entityTypes: [entityType],
      childrenDepth: 1,
    }),
    [entityId, selectedTabType, entityType, pagination.limit, pagination.offset]
  );

  // Query to get tags for selected tab type
  const { loading: tagsQueryLoading, refetch } = useQuery(searchAppliedTags, {
    variables: searchAppliedTagsVariables,
    skip: !entityId || !selectedTabType || countsLoading,
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "cache-and-network",
    onCompleted: (response) => {
      if (!isEmpty(response?.results?.entityTags)) {
        // Keep the full EntityTag structure with id, tagId, entityId, entityTagAttributes, and nested tag
        const newData = orderBy(
          response?.results?.entityTags,
          "tag.displayValue"
        );

        // Only dispatch if data actually changed to prevent unnecessary updates
        if (!isEqual(lastFetchedDataRef.current, newData)) {
          lastFetchedDataRef.current = newData;
          dispatch(setTagsData(newData));
          dispatch(
            setPagination({
              totalCount: response?.results?.totalCount || 0,
            })
          );
        }
      }
    },
    onError: (queryError) => {
      dispatch(setError(queryError));
    },
  });

  return {
    countsLoading,
    tagsQueryLoading,
    loading: countsLoading || tagsQueryLoading,
    refetch,
  };
};
